import {useEffect, useMemo, useRef} from 'react';

/** Detects macOS / iOS for the command-palette modifier label. */
export const useCommandSearchShortcutLabel = (): string => {
    return useMemo(() => {
        if (typeof navigator === 'undefined') {
            return 'Ctrl+K';
        }
        return /Mac|iPhone|iPod|iPad/i.test(navigator.platform) ? '⌘K' : 'Ctrl+K';
    }, []);
};

interface UseCommandSearchKeyboardOptions {
    open: boolean;
    onOpen: () => void;
    onClose: () => void;
}

/** Registers the global ⌘K / Ctrl+K toggle listener. */
export const useCommandSearchKeyboard = ({
    open,
    onOpen,
    onClose,
}: UseCommandSearchKeyboardOptions): void => {
    // Read the latest open state inside the listener so rapid toggles never race the effect rebind.
    const openRef = useRef(open);
    openRef.current = open;

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            const modifier = event.metaKey || event.ctrlKey;
            if (!modifier || event.key.toLowerCase() !== 'k') {
                return;
            }
            // ⌘K / Ctrl+K is a global command-palette toggle regardless of focus target.
            event.preventDefault();
            event.stopPropagation();
            if (openRef.current) {
                onClose();
            } else {
                onOpen();
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onClose, onOpen]);
};

/** @deprecated Use useCommandSearchShortcutLabel */
export const usePanelCommandShortcutLabel = useCommandSearchShortcutLabel;
